/* eslint-disable no-continue */
/* eslint-disable consistent-return */
import { Button, Calendar1Icon } from "components";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../appContent";
import { RightArrowIcon, VerifiedCheckIcon } from "../Icons";

// Dot tunnel constants — match original visual intent
const DOTS_PER_RING = 40;
const HALF_W = 150;
const HALF_H = 100;
const SPACING_Z = 30;
const RINGS = Math.ceil(400 / SPACING_Z) + 2; // 16
const WRAP_Z = RINGS * SPACING_Z; // 480
const SPEED = 0.1;
const PERSPECTIVE = 1000;

/** Pre-compute initial dot positions into typed arrays (avoids GC pressure). */
function buildDots() {
  const total = RINGS * DOTS_PER_RING;
  const xs = new Float32Array(total);
  const ys = new Float32Array(total);
  const zs = new Float32Array(total);
  let idx = 0;
  for (let ring = 0; ring < RINGS; ring++) {
    for (let dot = 0; dot < DOTS_PER_RING; dot++) {
      const side = Math.floor(dot / (DOTS_PER_RING / 4));
      const progress = (dot % (DOTS_PER_RING / 4)) / (DOTS_PER_RING / 4);
      let x = 0;
      let y = 0;
      if (side === 0) {
        x = -HALF_W + progress * 2 * HALF_W;
        y = -HALF_H;
      } else if (side === 1) {
        x = HALF_W;
        y = -HALF_H + progress * 2 * HALF_H;
      } else if (side === 2) {
        x = HALF_W - progress * 2 * HALF_W;
        y = HALF_H;
      } else {
        x = -HALF_W;
        y = HALF_H - progress * 2 * HALF_H;
      }
      xs[idx] = x;
      ys[idx] = y;
      zs[idx] = 900 - ring * SPACING_Z;
      idx++;
    }
  }
  return { xs, ys, zs, total };
}

const Herosection = () => {
  const { heroSectionRef, token } = useAppContext();
  const navigate = useNavigate();
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = heroSectionRef.current;
    if (!canvas || !container) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const { xs, ys, zs, total } = buildDots();
    let rafId = 0;
    let logicalW = 0;
    let logicalH = 0;

    const resize = () => {
      logicalW = container.offsetWidth || window.innerWidth;
      logicalH = container.offsetHeight || window.innerHeight;
      canvas.width = logicalW * dpr;
      canvas.height = logicalH * dpr;
      canvas.style.width = `${logicalW}px`;
      canvas.style.height = `${logicalH}px`;
    };
    resize();
    window.addEventListener("resize", resize, { passive: true });

    const animate = () => {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, logicalW, logicalH);

      const cx = logicalW / 2;
      const cy = logicalH / 2;

      for (let i = 0; i < total; i++) {
        zs[i] += SPEED;
        if (zs[i] > 1000) zs[i] -= WRAP_Z;

        const z = zs[i];
        const denom = PERSPECTIVE - z;
        if (denom <= 15) continue; // near-clip

        const scale = PERSPECTIVE / denom;
        const sx = cx + xs[i] * scale;
        const sy = cy + ys[i] * scale;
        const r = Math.max(0.4, 0.75 * scale);
        // Alpha: brightens as dots approach, fades at near-clip boundary
        const alpha = Math.min(0.85, scale * 0.1) * Math.min(1, denom / 60);

        ctx.beginPath();
        ctx.arc(sx, sy, r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0,179,179,${alpha.toFixed(3)})`;
        ctx.fill();
      }

      rafId = requestAnimationFrame(animate);
    };

    rafId = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", resize);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      ref={heroSectionRef}
      className="Herosection absolute h-full w-full perspective-[1000px] z-50"
      style={{
        background:
          "radial-gradient(circle,rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 1) 60%)",
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 9999,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          gap: 40,
        }}
      >
        <div className="inline-flex flex-col justify-start items-center gap-10 ">
          <div className="self-stretch flex flex-col justify-start items-center gap-5">
            <div className="px-3 py-2.5 bg-[#222222]/40 rounded-[100px] backdrop-blur-[20px] inline-flex justify-center items-center gap-2">
              <VerifiedCheckIcon width={20} height={20} />
              <div className="text-center text-neutral-100 text-sm">
                Simplify. Streamline. Succeed
              </div>
            </div>
            <h1 className="self-stretch text-center text-white text-[48px] font-semibold">
              One Platform to Power Your Entire Wealth Business
            </h1>
            <div className="w-[600px] text-center text-neutral-300 text-base leading-7">
              Designed for Family Offices, Financial Advisors, Portfolio
              Managers, Brokers, and Wealth Businesses seeking to manage and
              scale their business.
            </div>
          </div>
          <div className="inline-flex justify-center items-center gap-4 z-50">
            <Button
              href="https://calendly.com/gridkey/introduction-to-gridkey-platform?month=2024-01"
              target="_blank"
              color="dark"
              variant="outlined"
              sx={{ p: 2 }}
              startIcon={<Calendar1Icon />}
            >
              Request a Demo
            </Button>
            <Button
              variant="landingBtn"
              endIcon={<RightArrowIcon width={20} height={20} color="#fff" />}
              onClick={() => {
                navigate(token ? "/app/dashboard" : "/login");
              }}
            >
              {token ? "Get Started" : "Login / Register"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Herosection;
